// ACCESSIBILITY LOGIC FOR OIC SITE

// Variables de estado
let fontSizeRatio = parseFloat(localStorage.getItem('font-size-ratio')) || 1.0;
let highContrastActive = localStorage.getItem('high-contrast') === 'true';
let ttsActive = localStorage.getItem('tts-active') === 'true';
let currentLanguage = localStorage.getItem('accessibility-lang') || 'es';
let ttsTimeout = null;
let currentUtterance = null;

// Inicialización de la UI y los listeners al cargar el DOM
document.addEventListener('DOMContentLoaded', () => {
    initFontResize();
    initHighContrast();
    initTTS();
    initTranslation();
    initExtendedAccessibility();
    initShortcuts();
    syncUIControls();
});

// ============================================================================
// 1. CONTROL DE TAMAÑO DE FUENTE
// ============================================================================
function initFontResize() {
    applyFontResize();
    
    // Asignar eventos a los botones de tamaño de fuente
    document.querySelectorAll('.btn-font-increase').forEach(btn => {
        btn.addEventListener('click', () => {
            if (fontSizeRatio < 1.30) {
                fontSizeRatio = fontSizeRatio === 1.0 ? 1.15 : 1.30;
                localStorage.setItem('font-size-ratio', fontSizeRatio);
                applyFontResize();
                syncUIControls();
            }
        });
    });

    document.querySelectorAll('.btn-font-decrease').forEach(btn => {
        btn.addEventListener('click', () => {
            if (fontSizeRatio > 1.0) {
                fontSizeRatio = fontSizeRatio === 1.30 ? 1.15 : 1.0;
                localStorage.setItem('font-size-ratio', fontSizeRatio);
                applyFontResize();
                syncUIControls();
            }
        });
    });

    document.querySelectorAll('.btn-font-reset').forEach(btn => {
        btn.addEventListener('click', () => {
            fontSizeRatio = 1.0;
            localStorage.setItem('font-size-ratio', fontSizeRatio);
            applyFontResize();
            syncUIControls();
        });
    });
}

function applyFontResize() {
    if (fontSizeRatio === 1.0) {
        document.documentElement.style.fontSize = '';
    } else {
        document.documentElement.style.fontSize = (fontSizeRatio * 100) + '%';
    }
}

// ============================================================================
// 2. ALTO CONTRASTE
// ============================================================================
function initHighContrast() {
    applyHighContrast();

    document.querySelectorAll('.btn-toggle-contrast').forEach(btn => {
        btn.addEventListener('click', () => {
            highContrastActive = !highContrastActive;
            localStorage.setItem('high-contrast', highContrastActive);
            applyHighContrast();
            syncUIControls();
        });
    });
}

function applyHighContrast() {
    if (highContrastActive) {
        document.documentElement.classList.add('high-contrast');
    } else {
        document.documentElement.classList.remove('high-contrast');
    }
}

// ============================================================================
// 3. TEXTO A VOZ (TTS)
// ============================================================================
// ============================================================================
// 3. TEXTO A VOZ (TTS)
// ============================================================================
let currentReadingElement = null;
let currentHoveredElement = null;

// Desbloquear audio en la primera interacción del usuario (Autoplay Policy Compliance)
let audioUnlocked = false;
function unlockAudio() {
    if (!audioUnlocked && window.speechSynthesis) {
        try {
            window.speechSynthesis.resume();
            audioUnlocked = true;
        } catch (e) {}
    }
}
window.addEventListener('click', unlockAudio, { once: true });
window.addEventListener('keydown', unlockAudio, { once: true });
window.addEventListener('touchstart', unlockAudio, { once: true });

function initTTS() {
    document.querySelectorAll('.btn-toggle-tts').forEach(btn => {
        btn.addEventListener('click', () => {
            unlockAudio();
            ttsActive = !ttsActive;
            localStorage.setItem('tts-active', ttsActive);
            syncUIControls();
            if (!ttsActive) {
                cancelSpeech();
            } else {
                speakText("Lector de voz activado. Pase el cursor sobre los textos para escucharlos.");
            }
        });
    });

    // Agregar eventos de hover para lectura a elementos individuales de contenido
    // Incluimos th y td para que todas las tablas y celdas del sitio sean legibles individualmente
    const textSelectors = 'p, h1, h2, h3, h4, h5, h6, li, button, label, a, strong, em, b, i, span, img, figcaption, dt, dd, time, th, td, [role="heading"], [data-tts]';
    
    document.addEventListener('mouseover', (e) => {
        if (!ttsActive) return;
        
        // Buscar el elemento de texto o contenido individual más cercano
        let target = e.target.closest(textSelectors);

        // Si no coincide directamente con un selector estándar pero es un elemento contenedor con texto
        // (ej. cajas <div> estilizadas como tarjetas o contenedores con texto mixto e inline)
        if (!target && e.target && e.target.nodeType === Node.ELEMENT_NODE) {
            // Si el contenedor tiene un único hijo de texto o bloque (ej. un único <p>), apuntar directamente a ese hijo
            if (e.target.children.length === 1 && e.target.firstElementChild.matches(textSelectors)) {
                target = e.target.firstElementChild;
            } else {
                // Verificar si es un contenedor sin bloques anidados (solo texto directo o elementos inline como <strong>, <span>)
                const hasBlockChild = e.target.querySelector('p, h1, h2, h3, h4, h5, h6, ul, ol, table, div, section, article, nav, header, footer, aside');
                const directText = (e.target.innerText || e.target.textContent || '').trim();
                if (!hasBlockChild && directText.length > 0 && directText !== '-' && directText !== '—') {
                    target = e.target;
                }
            }
        }

        if (!target) return;

        // Si el elemento tiene un aria-label o data-tts explícito propio, conservarlo para lectura directa
        const hasExplicitTts = target.hasAttribute('aria-label') || target.hasAttribute('data-tts');

        if (!hasExplicitTts) {
            // Si el elemento vive dentro de un enlace (a) o botón interactivo, preferimos enfocar el enlace/botón
            const interactiveParent = target.closest('a, button');
            const blockParent = target.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote, th, td, dd, dt');
            const isInline = ['span', 'strong', 'em', 'b', 'i'].includes(target.tagName.toLowerCase());

            if (interactiveParent) {
                target = interactiveParent;
            } else if (blockParent && isInline && blockParent !== target) {
                target = blockParent;
            } else if (!blockParent && isInline && target.parentElement && target.parentElement.nodeType === Node.ELEMENT_NODE) {
                // Si el elemento inline está dentro de un contenedor (ej. <div>) que actúa como bloque de texto
                const hasNestedBlock = target.parentElement.querySelector('p, h1, h2, h3, h4, h5, h6, ul, ol, table, div, section, article');
                if (!hasNestedBlock) {
                    target = target.parentElement;
                }
            }
        }

        // Si el objetivo es una celda (td/th) que contiene un único enlace o botón principal (ej. botón de resolución o "Ver"),
        // enfocar directamente ese botón/enlace para una interacción visual más precisa
        if (target && (target.tagName.toLowerCase() === 'td' || target.tagName.toLowerCase() === 'th')) {
            const interactiveElements = target.querySelectorAll('a, button');
            if (interactiveElements.length === 1) {
                const singleInteractive = interactiveElements[0];
                const textWithoutInteractive = Array.from(target.childNodes)
                    .filter(node => node !== singleInteractive && !singleInteractive.contains(node))
                    .map(node => node.textContent || '')
                    .join('')
                    .replace(/\s+/g, ' ')
                    .trim();
                if (!textWithoutInteractive) {
                    target = singleInteractive;
                }
            }
        }

        // Evitar reiniciar si seguimos sobre el mismo elemento
        if (currentHoveredElement === target) {
            return;
        }

        currentHoveredElement = target;
        clearTimeout(ttsTimeout);

        ttsTimeout = setTimeout(() => {
            const textToRead = extractAccessibleText(target);
            if (textToRead && textToRead.length > 0) {
                speakText(textToRead, target);
            }
        }, 120);
    });

    document.addEventListener('mouseout', (e) => {
        if (!ttsActive) return;
        
        // Si el cursor se movió a un nodo hijo del mismo contenedor (como los <font> de Google), no cancelar la lectura
        if (e.relatedTarget && currentHoveredElement && currentHoveredElement.contains(e.relatedTarget)) {
            return;
        }

        currentHoveredElement = null;
        clearTimeout(ttsTimeout);
    });
}

// Extrae el texto accesible de un elemento, manejando imágenes (alt), enlaces con imágenes y textos dinámicos
function extractAccessibleText(target) {
    if (!target) return '';

    // 1. Imagen directa
    if (target.tagName.toLowerCase() === 'img') {
        const alt = target.getAttribute('alt') || target.getAttribute('title') || target.getAttribute('aria-label');
        if (alt && alt.trim().length > 0 && alt.trim() !== '-') {
            return alt.toLowerCase().startsWith('imagen') ? alt.trim() : `Imagen: ${alt.trim()}`;
        }
        return 'Imagen informativa';
    }

    // 2. Enlace o contenedor que envuelve una imagen (sin texto directo)
    const innerImg = target.querySelector('img');
    const directText = (target.textContent || '').trim();
    if (innerImg && (!directText || directText.length === 0)) {
        const alt = innerImg.getAttribute('alt') || innerImg.getAttribute('title') || innerImg.getAttribute('aria-label') || target.getAttribute('aria-label') || target.getAttribute('title');
        if (alt && alt.trim().length > 0 && alt.trim() !== '-') {
            return alt.toLowerCase().startsWith('imagen') ? alt.trim() : `Imagen: ${alt.trim()}`;
        }
        return 'Imagen informativa';
    }

    // 3. Atributo aria-label explícito
    const ariaLabel = target.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim().length > 0) {
        return ariaLabel.trim();
    }

    // 4. Texto del elemento (usando textContent para preservar mayúsculas y minúsculas naturales)
    let rawText = target.innerText?.trim() || target.textContent?.trim() || target.getAttribute('title') || target.getAttribute('alt') || '';
    rawText = rawText.replace(/\s+/g, ' ').trim();

    if (rawText === '-' || rawText === '—' || rawText.length === 0) {
        return '';
    }

    // Optimización de fechas para lectura natural en español (ej. "18/sep/2026" o "18/09/2026")
    if (currentLanguage !== 'en') {
        const meses = {
            'ene': 'enero', 'feb': 'febrero', 'mar': 'marzo', 'abr': 'abril',
            'may': 'mayo', 'jun': 'junio', 'jul': 'julio', 'ago': 'agosto',
            'sep': 'septiembre', 'oct': 'octubre', 'nov': 'noviembre', 'dic': 'diciembre',
            '01': 'enero', '02': 'febrero', '03': 'marzo', '04': 'abril',
            '05': 'mayo', '06': 'junio', '07': 'julio', '08': 'agosto',
            '09': 'septiembre', '10': 'octubre', '11': 'noviembre', '12': 'diciembre'
        };
        rawText = rawText.replace(/\b(\d{1,2})\/(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic|\d{2})\/(\d{4})\b/gi, (match, dia, mes, anio) => {
            const mesNombre = meses[mes.toLowerCase()];
            return mesNombre ? `${dia} de ${mesNombre} de ${anio}` : match;
        });
    }

    // Si todo el texto está en mayúsculas sostenidas y es largo (> 25 caracteres),
    // normalizar a minúsculas con inicial mayúscula para síntesis fluida en español
    if (rawText.length > 25 && rawText === rawText.toUpperCase() && /[A-ZÁÉÍÓÚÑ]/.test(rawText)) {
        rawText = rawText.charAt(0).toUpperCase() + rawText.slice(1).toLowerCase();
    }

    return rawText;
}

// Precargar voces del sintetizador
let cachedVoices = [];
function updateVoices() {
    if (window.speechSynthesis) {
        cachedVoices = window.speechSynthesis.getVoices();
    }
}
if (window.speechSynthesis) {
    updateVoices();
    window.speechSynthesis.onvoiceschanged = updateVoices;
}

function speakText(text, targetElement = null) {
    if (!window.speechSynthesis) return;
    
    if (window.speechSynthesis.paused) {
        try {
            window.speechSynthesis.resume();
        } catch (e) {}
    }

    // Cancela inmediatamente cualquier audio previo
    window.speechSynthesis.cancel();

    // Limpiar destaque anterior si lo hubiera
    if (currentReadingElement && currentReadingElement !== targetElement) {
        currentReadingElement.classList.remove('accessibility-reading-highlight');
        currentReadingElement = null;
    }

    // Limpiar saltos de línea adicionales
    const cleanText = text.replace(/\s+/g, ' ').trim();
    if (!cleanText) return;
    
    currentUtterance = new SpeechSynthesisUtterance(cleanText);
    currentUtterance.volume = 1.0;
    currentUtterance.rate = 1.0;
    
    // Guardar referencia global para evitar que el Garbage Collector de Chrome cancele el audio
    window._activeUtterance = currentUtterance;
    
    // Determinar estrictamente el idioma basándonos en la preferencia activa del usuario
    const isEnglish = (currentLanguage === 'en');
    
    const voices = cachedVoices.length ? cachedVoices : (window.speechSynthesis.getVoices() || []);
    
    if (isEnglish) {
        currentUtterance.lang = 'en-US';
        if (voices && voices.length > 0) {
            const englishVoice = voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en'));
            if (englishVoice) {
                currentUtterance.voice = englishVoice;
            }
        }
    } else {
        currentUtterance.lang = 'es-MX';
        if (voices && voices.length > 0) {
            const spanishVoice = voices.find(v => v.lang && v.lang.toLowerCase().startsWith('es'));
            if (spanishVoice) {
                currentUtterance.voice = spanishVoice;
            }
        }
    }

    // Gestionar destaque visual si hay un elemento objetivo
    if (targetElement) {
        currentReadingElement = targetElement;
        currentReadingElement.classList.add('accessibility-reading-highlight');
        
        currentUtterance.onend = () => {
            if (targetElement) {
                targetElement.classList.remove('accessibility-reading-highlight');
            }
            if (currentReadingElement === targetElement) {
                currentReadingElement = null;
            }
            window._activeUtterance = null;
        };
        
        currentUtterance.onerror = (e) => {
            console.warn('TTS Speech error:', e);
            if (targetElement) {
                targetElement.classList.remove('accessibility-reading-highlight');
            }
            if (currentReadingElement === targetElement) {
                currentReadingElement = null;
            }
            window._activeUtterance = null;
        };
    }
    
    // En Chromium, un micro-delay tras cancel() previene cancelación instantánea
    setTimeout(() => {
        if (window.speechSynthesis) {
            window.speechSynthesis.speak(currentUtterance);
        }
    }, 15);
}

function cancelSpeech() {
    if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
    }
    // Limpiar destaque visual activo al cancelar
    if (currentReadingElement) {
        currentReadingElement.classList.remove('accessibility-reading-highlight');
        currentReadingElement = null;
    }
}

// ============================================================================
// 4. ATAJOS DE TECLADO Y GUÍA VISUAL (MODAL ACCESIBLE)
// ============================================================================
let lastActiveElement = null;

function initShortcuts() {
    // Configurar modal de ayuda para atajos
    const shortcutModal = document.getElementById('accessibility-shortcut-modal');
    const closeBtn = document.getElementById('close-shortcut-modal');
    const closeBtnFooter = document.getElementById('close-shortcut-modal-footer');

    function toggleShortcutModal() {
        if (shortcutModal) {
            const isOpening = shortcutModal.classList.contains('hidden');
            shortcutModal.classList.toggle('hidden');
            shortcutModal.classList.toggle('flex');
            
            if (isOpening) {
                // Guardar el elemento activo que abrió el diálogo para regresar el foco después
                lastActiveElement = document.activeElement;
                
                speakText("Guía de atajos de teclado abierta. Presione escape para cerrarla.");
                
                // Mover el foco del teclado inmediatamente al botón de cierre
                setTimeout(() => {
                    if (closeBtn) {
                        closeBtn.focus();
                    }
                }, 50);
            } else {
                cancelSpeech();
                // Devolver el foco al elemento que activó el modal
                if (lastActiveElement) {
                    lastActiveElement.focus();
                }
            }
        }
    }

    document.querySelectorAll('.btn-show-shortcuts').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            toggleShortcutModal();
        });
    });

    if (closeBtn) {
        closeBtn.addEventListener('click', toggleShortcutModal);
    }

    if (closeBtnFooter) {
        // Asignar el mismo controlador en vez del onclick inline
        closeBtnFooter.removeAttribute('onclick');
        closeBtnFooter.addEventListener('click', (e) => {
            e.preventDefault();
            toggleShortcutModal();
        });
    }

    // Cerrar modal al hacer click fuera del contenedor de contenido
    if (shortcutModal) {
        shortcutModal.addEventListener('click', (e) => {
            if (e.target === shortcutModal) {
                toggleShortcutModal();
            }
        });
    }

    // Enlace Skip-Link en el DOM
    document.querySelectorAll('.skip-link').forEach(link => {
        link.addEventListener('click', (e) => {
            jumpToMainContent(e);
        });
    });

    // Escuchador global de teclado
    document.addEventListener('keydown', (e) => {
        // Alt + K: Abrir/Cerrar modal de ayuda
        if (e.altKey && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            toggleShortcutModal();
        }
        
        // Escape: Cerrar modal si está abierto
        if (e.key === 'Escape' && shortcutModal && !shortcutModal.classList.contains('hidden')) {
            toggleShortcutModal();
        }

        // Atajos de navegación directa (Alt + letra)
        if (e.altKey) {
            const key = e.key.toLowerCase();
            
            // Alt + I: Ir a Inicio
            if (key === 'i' || key === 'h') {
                e.preventDefault();
                window.location.href = '/';
            }
            
            // Alt + U: Ir a la UEAAG
            if (key === 'u') {
                e.preventDefault();
                window.location.href = '/ueaag/';
            }
            
            // Alt + G: Ir a la Autoridad Garante
            if (key === 'g') {
                e.preventDefault();
                window.location.href = '/agtd/';
            }
            
            // Alt + C: Ir a Contáctanos
            if (key === 'c') {
                e.preventDefault();
                // Determinar la url correcta según la sección
                if (window.location.pathname.includes('/agtd/')) {
                    window.location.href = '/agtd/contacto/agtd/';
                } else if (window.location.pathname.includes('/ueaag/')) {
                    window.location.href = '/ueaag/contacto/';
                } else {
                    window.location.href = '/contactanos/';
                }
            }

            // Alt + M: Saltar al contenido principal
            if (key === 'm') {
                e.preventDefault();
                jumpToMainContent(e);
            }
        }
    });
}

function showAccessibilityToast(message) {
    let toast = document.getElementById('accessibility-toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'accessibility-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.className = 'fixed bottom-6 right-6 z-[99999] bg-[#003057] text-white text-xs sm:text-sm font-montserrat font-semibold px-4 py-2.5 rounded-lg shadow-2xl border border-cyan-400/40 flex items-center gap-2.5 transform transition-all duration-300 pointer-events-none opacity-0 translate-y-3';
        document.body.appendChild(toast);
    }
    toast.innerHTML = `
        <svg class="w-4 h-4 text-[#00c0b4] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
        </svg>
        <span>${message}</span>
    `;
    toast.classList.remove('opacity-0', 'translate-y-3');
    toast.classList.add('opacity-100', 'translate-y-0');

    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
        toast.classList.remove('opacity-100', 'translate-y-0');
        toast.classList.add('opacity-0', 'translate-y-3');
    }, 2400);
}

function jumpToMainContent(e) {
    if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
    }
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    mainContent.setAttribute('tabindex', '-1');

    // Desplazamiento suave visible hacia el inicio del contenido
    const yOffset = -24;
    const y = mainContent.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });

    // Foco de teclado en el contenedor principal
    mainContent.focus({ preventScroll: true });

    // Resaltado visual accesible en el contenedor
    mainContent.classList.add('skip-target-focused');
    setTimeout(() => {
        mainContent.classList.remove('skip-target-focused');
    }, 2000);

    // Notificación visual en pantalla
    showAccessibilityToast('Contenido principal enfocado (Alt + M)');

    // Anuncio auditivo si el lector TTS está activo
    if (ttsActive) {
        speakText('Saltando al contenido principal');
    }
}

// ============================================================================
// 5. SINCRONIZACIÓN DE LA UI
// ============================================================================
function syncUIControls() {
    // Sincronizar clases y estados visuales en los botones/indicadores de accesibilidad
    
    // 1. Botones de fuente
    document.querySelectorAll('.btn-font-increase').forEach(btn => {
        if (fontSizeRatio >= 1.30) {
            btn.classList.add('opacity-50', 'cursor-not-allowed');
            btn.setAttribute('aria-disabled', 'true');
        } else {
            btn.classList.remove('opacity-50', 'cursor-not-allowed');
            btn.removeAttribute('aria-disabled');
        }
    });

    document.querySelectorAll('.btn-font-decrease').forEach(btn => {
        if (fontSizeRatio <= 1.0) {
            btn.classList.add('opacity-50', 'cursor-not-allowed');
            btn.setAttribute('aria-disabled', 'true');
        } else {
            btn.classList.remove('opacity-50', 'cursor-not-allowed');
            btn.removeAttribute('aria-disabled');
        }
    });

    // 2. Botón de contraste (Activo/Inactivo)
    document.querySelectorAll('.btn-toggle-contrast').forEach(btn => {
        if (highContrastActive) {
            btn.classList.add('bg-yellow-400', 'text-black', 'border-yellow-500');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
        } else {
            btn.classList.remove('bg-yellow-400', 'text-black', 'border-yellow-500');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
        }
    });

    // 3. Botón de Lector de voz (TTS)
    document.querySelectorAll('.btn-toggle-tts').forEach(btn => {
        if (ttsActive) {
            btn.classList.add('bg-green-500', 'text-white', 'border-green-600');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
        } else {
            btn.classList.remove('bg-green-500', 'text-white', 'border-green-600');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
        }
    });

    // 4. Botón de Traducción (Inglés/Español)
    document.querySelectorAll('.btn-toggle-translation').forEach(btn => {
        if (currentLanguage === 'en') {
            btn.classList.add('bg-blue-500', 'text-white', 'border-blue-600');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
            btn.setAttribute('title', 'Ver en Español (Spanish)');
        } else {
            btn.classList.remove('bg-blue-500', 'text-white', 'border-blue-600');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('title', 'Traducir a Inglés (English)');
        }
    });

    // 5. Botón de Reducir Movimiento / Pausar Carrusel
    document.querySelectorAll('.btn-toggle-motion').forEach(btn => {
        if (isMotionReduced) {
            btn.classList.add('bg-purple-600', 'text-white', 'border-purple-700');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
            btn.setAttribute('title', 'Reanudar movimiento y slider (Pausado)');
        } else {
            btn.classList.remove('bg-purple-600', 'text-white', 'border-purple-700');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('title', 'Detener animaciones y carrusel');
        }
    });

    // 6. Botón de Fuente Dislexia
    document.querySelectorAll('.btn-toggle-dyslexic').forEach(btn => {
        if (isDyslexicFont) {
            btn.classList.add('bg-indigo-600', 'text-white', 'border-indigo-700');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
        } else {
            btn.classList.remove('bg-indigo-600', 'text-white', 'border-indigo-700');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
        }
    });

    // 7. Botón de Regla de Lectura
    document.querySelectorAll('.btn-toggle-ruler').forEach(btn => {
        if (isReadingRuler) {
            btn.classList.add('bg-teal-600', 'text-white', 'border-teal-700');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
        } else {
            btn.classList.remove('bg-teal-600', 'text-white', 'border-teal-700');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
        }
    });

    // 8. Botón de Escala de Grises
    document.querySelectorAll('.btn-toggle-grayscale').forEach(btn => {
        if (isGrayscaleMode) {
            btn.classList.add('bg-gray-800', 'text-white', 'border-gray-900');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
        } else {
            btn.classList.remove('bg-gray-800', 'text-white', 'border-gray-900');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
        }
    });

    // 9. Botón de Resaltar Enlaces
    document.querySelectorAll('.btn-toggle-links').forEach(btn => {
        if (isHighlightLinks) {
            btn.classList.add('bg-pink-600', 'text-white', 'border-pink-700');
            btn.classList.remove('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'true');
        } else {
            btn.classList.remove('bg-pink-600', 'text-white', 'border-pink-700');
            btn.classList.add('bg-gray-100', 'text-gray-700', 'dark:bg-gray-700', 'dark:text-gray-300');
            btn.setAttribute('aria-pressed', 'false');
        }
    });

    // 10. Botón de Modo Oscuro / Claro
    const isCurrentlyDark = document.documentElement.classList.contains('dark');
    document.querySelectorAll('#theme-toggle, .theme-toggle').forEach(btn => {
        const label = isCurrentlyDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro';
        const tooltip = isCurrentlyDark ? 'Modo claro' : 'Modo oscuro';
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', tooltip);
    });
}

// ============================================================================
// 6. TRADUCCIÓN AL INGLÉS (GOOGLE TRANSLATE DISCRETO)
// ============================================================================
function initTranslation() {
    document.querySelectorAll('.btn-toggle-translation').forEach(btn => {
        btn.addEventListener('click', () => {
            toggleTranslation();
        });
    });

    // Si al cargar la página el idioma guardado es inglés, forzar lang='en' para el sintetizador TTS
    if (currentLanguage === 'en') {
        document.documentElement.setAttribute('lang', 'en');
    } else {
        document.documentElement.setAttribute('lang', 'es');
    }
}

function toggleTranslation() {
    if (currentLanguage === 'es') {
        currentLanguage = 'en';
        localStorage.setItem('accessibility-lang', 'en');
        // Establecer cookie oficial de Google Translate para traducir a inglés
        document.cookie = "googtrans=/es/en; path=/;";
        if (location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
            document.cookie = "googtrans=/es/en; domain=" + location.hostname + "; path=/;";
        }
    } else {
        currentLanguage = 'es';
        localStorage.setItem('accessibility-lang', 'es');
        // Limpiar y restablecer las cookies de Google Translate para restaurar al español
        document.cookie = "googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
        document.cookie = "googtrans=/es/es; path=/;";
        if (location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
            document.cookie = "googtrans=; domain=" + location.hostname + "; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
            document.cookie = "googtrans=/es/es; domain=" + location.hostname + "; path=/;";
        }
    }

    // Recargar la página para aplicar o remover limpiamente la traducción sin dejar rastros de barras flotantes
    location.reload();
}

// ============================================================================
// 7. SUITE EXTENDIDA (PAUSAR MOVIMIENTO, DISLEXIA, REGLA DE LECTURA, ESCALA DE GRISES, RESALTAR ENLACES)
// ============================================================================

let isMotionReduced = localStorage.getItem('accessibility-reduce-motion') === 'true';
let isDyslexicFont = localStorage.getItem('accessibility-dyslexic-font') === 'true';
let isReadingRuler = localStorage.getItem('accessibility-reading-ruler') === 'true';
let isGrayscaleMode = localStorage.getItem('accessibility-grayscale-mode') === 'true';
let isHighlightLinks = localStorage.getItem('accessibility-highlight-links') === 'true';

function handleSwiperAutoplay(stop) {
    // 1. Pausar / reanudar Swiper instanciado en nodos HTML (.swiper, .mySwiper)
    const swiperElements = document.querySelectorAll('.swiper, .mySwiper');
    swiperElements.forEach(el => {
        if (el.swiper && el.swiper.autoplay) {
            if (stop) {
                el.swiper.autoplay.stop();
            } else {
                el.swiper.autoplay.start();
            }
        }
    });

    // 2. Pausar / reanudar instancia global window.swiper
    if (window.swiper && window.swiper.autoplay) {
        if (stop) {
            window.swiper.autoplay.stop();
        } else {
            window.swiper.autoplay.start();
        }
    }
}

function initExtendedAccessibility() {
    // 1. Reducir Movimiento / Pausar Animaciones
    if (isMotionReduced) {
        document.documentElement.classList.add('reduce-motion');
        setTimeout(() => handleSwiperAutoplay(true), 600);
    }
    document.querySelectorAll('.btn-toggle-motion').forEach(btn => {
        btn.addEventListener('click', toggleMotion);
    });

    // 2. Fuente para Dislexia
    if (isDyslexicFont) {
        document.documentElement.classList.add('dyslexic-font');
    }
    document.querySelectorAll('.btn-toggle-dyslexic').forEach(btn => {
        btn.addEventListener('click', toggleDyslexicFont);
    });

    // 3. Regla de Lectura Visual
    if (isReadingRuler) {
        setRulerVisibility(true);
    }
    document.querySelectorAll('.btn-toggle-ruler').forEach(btn => {
        btn.addEventListener('click', toggleReadingRuler);
    });
    window.addEventListener('mousemove', (e) => {
        const ruler = document.getElementById('accessibility-reading-ruler');
        if (ruler && isReadingRuler) {
            ruler.style.top = e.clientY + 'px';
        }
    });

    // 4. Escala de Grises
    if (isGrayscaleMode) {
        document.documentElement.classList.add('grayscale-mode');
    }
    document.querySelectorAll('.btn-toggle-grayscale').forEach(btn => {
        btn.addEventListener('click', toggleGrayscaleMode);
    });

    // 5. Resaltar Enlaces
    if (isHighlightLinks) {
        document.documentElement.classList.add('highlight-links');
    }
    document.querySelectorAll('.btn-toggle-links').forEach(btn => {
        btn.addEventListener('click', toggleHighlightLinks);
    });

    syncUIControls();
}

function toggleMotion() {
    isMotionReduced = !isMotionReduced;
    localStorage.setItem('accessibility-reduce-motion', isMotionReduced);
    document.documentElement.classList.toggle('reduce-motion', isMotionReduced);
    handleSwiperAutoplay(isMotionReduced);
    syncUIControls();
}

function toggleDyslexicFont() {
    isDyslexicFont = !isDyslexicFont;
    localStorage.setItem('accessibility-dyslexic-font', isDyslexicFont);
    document.documentElement.classList.toggle('dyslexic-font', isDyslexicFont);
    syncUIControls();
}

function toggleReadingRuler() {
    isReadingRuler = !isReadingRuler;
    localStorage.setItem('accessibility-reading-ruler', isReadingRuler);
    setRulerVisibility(isReadingRuler);
    syncUIControls();
}

function setRulerVisibility(visible) {
    const ruler = document.getElementById('accessibility-reading-ruler');
    if (ruler) {
        ruler.style.display = visible ? 'block' : 'none';
    }
}

function toggleGrayscaleMode() {
    isGrayscaleMode = !isGrayscaleMode;
    localStorage.setItem('accessibility-grayscale-mode', isGrayscaleMode);
    document.documentElement.classList.toggle('grayscale-mode', isGrayscaleMode);
    syncUIControls();
}

function toggleHighlightLinks() {
    isHighlightLinks = !isHighlightLinks;
    localStorage.setItem('accessibility-highlight-links', isHighlightLinks);
    document.documentElement.classList.toggle('highlight-links', isHighlightLinks);
    syncUIControls();
}

// Exponer función de vocalización accesible para otros módulos (ej. dark-mode.js)
window.speakAccessibilityText = function(text) {
    if (ttsActive) {
        speakText(text);
    }
};
